import {
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Box,
  DrawerFooter,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { truncateUuid } from 'packages/common-frontend/utils';
import { FacilityDetails } from 'packages/loans/ui/FacilityDetails';
import { LoanDetails } from 'packages/loans/ui/LoanDetails';
import { TimelineStepper } from 'packages/transactions/ui/TimelineStepper';

type LoanDetailsSideDrawerHeaderProps = {
  loanName: string;
  loanId: string;
  onCloseDrawer: () => void;
};
const LoanDetailsSideDrawerHeader = ({
  loanName,
  loanId,
  onCloseDrawer,
}: LoanDetailsSideDrawerHeaderProps) => {
  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        m="24px 24px 0 24px"
        p="1rem"
      >
        <Typography variant="section-title" trim>
          {loanName
            ? `${loanName} (${truncateUuid(loanId)})`
            : `Loan ${truncateUuid(loanId)}`}
        </Typography>
        <Button variant="v2-ghost" onClick={onCloseDrawer}>
          <Icon icon="x" />
        </Button>
      </Flex>
      <Divider />
    </>
  );
};

export const LoanDetailsSideDrawerBody = ({
  loan,
}: {
  loan: ResolvedLoanFragmentGQLType;
}) => {
  const timelineDisplaySteps = useMemo(() => {
    return [
      {
        id: '1',
        icon: 'sack-dollar',
        title: 'Funding Date',
        description: loan.dates.originationDate
          ? commonDate.fullDateStrToHumanDateString(loan.dates.originationDate)
          : '-',
      },
      {
        id: '2',
        icon: 'hourglass-start',
        title: 'Next Payment Date',
        description: loan.invoices?.nextPaymentDate
          ? commonDate.fullDateStrToHumanDateString(
              loan.invoices?.nextPaymentDate,
            )
          : '-',
      },
      {
        id: '3',
        icon: 'hourglass-end',
        title: 'Maturity Date',
        description: loan.dates.maturityDate
          ? commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)
          : '-',
      },
    ];
  }, [
    loan.dates.originationDate,
    loan.dates.maturityDate,
    loan.invoices?.nextPaymentDate,
  ]);

  return (
    <Box>
      <TimelineStepper steps={timelineDisplaySteps} completedStepIndex={1} />
      <Divider my="40px" />
      <FacilityDetails loanLoading={false} loan={loan} showExternalFacilityId />
      <Divider my="40px" />
      <LoanDetails
        loading={false}
        loan={loan}
        showLoanNamingInfo
        hideExternalLoanId
      />
    </Box>
  );
};

type LoanDetailsSideDrawerProps = {
  loan: ResolvedLoanFragmentGQLType;
  isOpen: boolean;
  onClose: () => void;
};
export const LoanDetailsSideDrawer = ({
  loan,
  isOpen,
  onClose,
}: LoanDetailsSideDrawerProps) => {
  const { id: loanId } = loan;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="xl"
      // workaround for known chakra issue where drawer body is not scrollable
      // https://github.com/chakra-ui/chakra-ui/issues/1862
      blockScrollOnMount={false}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader padding="0">
          <LoanDetailsSideDrawerHeader
            loanName={loan.name}
            loanId={loan.id}
            onCloseDrawer={onClose}
          />
        </DrawerHeader>

        <DrawerBody p="40px">
          <LoanDetailsSideDrawerBody loan={loan} />
        </DrawerBody>
        <DrawerFooter>
          <Flex w="100%" justifyContent="flex-end">
            <Button
              as={ReactRouterLink}
              to={`/loans/${loanId}`}
              rightIcon={<Icon icon="chevron-right" />}
              variant="v2-ghost"
            >
              View Loan
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
